import React, { useCallback, useEffect, useMemo, useState } from 'react';

import alert from '../../helpers/alert';
import extractErrorMessage from '../../utils/extractErrorMessage';
import styles from './sheetSync.module.css';
import {
  getSheetSyncHistory,
  getSheetSyncSettings,
  rollbackSheetRun,
  rollbackToSheetRun,
  setSheetAutoSync,
} from '../../api/prices';
import {
  KIND_LABELS,
  STATUS_LABELS,
  canRollback,
  deltaText,
  formatDateTime,
  isBigJump,
  laterRollbackable,
  matchesFilter,
  money,
  summarizeRun,
} from './sheetSyncFormat';

const kindClass = (kind) =>
  [
    styles.kind,
    { created: styles.kindCreated, changed: styles.kindChanged, held: styles.kindHeld, problem: styles.kindProblem }[kind],
  ]
    .filter(Boolean)
    .join(' ');

// Автосинхронизация: переключатель живёт здесь, рядом с историей и откатами — отключить её нужно, когда возвращаешься
// к старой версии, иначе следующая проверка снова применит цены из таблицы
const AutoSyncPanel = ({ settings, busy, onToggle, confirming }) => {
  if (!settings) return null;
  const on = settings.autoSync;
  return (
    <div className={`${styles.autoPanel} ${on ? styles.autoOn : styles.autoOff}`}>
      <div className={styles.autoText}>
        <b>Автосинхронизация: {on ? 'включена' : 'выключена'}</b>
        <div className={styles.muted}>
          {on
            ? `Таблица проверяется примерно раз в ${settings.intervalMinutes} мин. Безопасные изменения применяются сами и пишутся сюда, спорные ждут подтверждения во вкладке «Таблица».`
            : 'Цены меняются только кнопкой «Применить изменения» во вкладке «Таблица». Если вы вернулись к старой версии, оставьте выключенной, иначе следующая проверка применит цены из таблицы снова.'}
        </div>
        <div className={styles.muted}>
          {settings.source === 'db'
            ? `Переключено на экране${settings.changedBy ? `: ${settings.changedBy}` : ''}${settings.changedAt ? `, ${formatDateTime(settings.changedAt)}` : ''}.`
            : 'Сейчас действует настройка из конфига сервера; выбор на экране её перекроет.'}
        </div>
      </div>
      <button
        type="button"
        className={on ? styles.mini : confirming ? styles.miniDanger : styles.miniPrimary}
        disabled={busy}
        title={on ? 'Остановить автоматическую проверку таблицы' : 'Запустить автоматическую проверку таблицы'}
        onClick={onToggle}
      >
        {on ? 'Выключить' : confirming ? 'Точно включить?' : 'Включить'}
      </button>
    </div>
  );
};

// Вкладка «История»: каждый запуск проверки таблицы и его дифф — когда, кто, что и на сколько изменилось.
// Отсюда же откат одного запуска, возврат к версии и переключатель автосинхронизации
const SheetHistory = () => {
  const [runs, setRuns] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const [query, setQuery] = useState('');
  // Откат и включение автосинхронизации меняют цены: второй клик подтверждает, через несколько секунд сбрасывается
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getSheetSyncHistory(50).then((res) => setRuns(res.data || [])),
      getSheetSyncSettings()
        .then((res) => setSettings(res.data))
        .catch(() => setSettings(null)),
    ])
      .catch((err) => {
        setRuns((prev) => prev || []);
        alert.error(extractErrorMessage(err) || 'Не удалось загрузить историю');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (confirm == null) return undefined;
    const timer = setTimeout(() => setConfirm(null), 5000);
    return () => clearTimeout(timer);
  }, [confirm]);

  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const ask = (kind, id = 0) => {
    if (confirm?.kind === kind && confirm?.id === id) {
      setConfirm(null);
      return true;
    }
    setConfirm({ kind, id });
    return false;
  };

  const rollback = async (runId) => {
    if (!ask('run', runId)) return;
    setBusy(true);
    try {
      const res = await rollbackSheetRun(runId);
      alert.success(`Запуск откачен: правил ${res.data.rules}, машин ${res.data.cars}, не тронуто правил ${res.data.rulesSkipped}`);
      load();
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось откатить запуск');
    } finally {
      setBusy(false);
    }
  };

  const rollbackTo = async (runId) => {
    if (!ask('to', runId)) return;
    setBusy(true);
    try {
      const res = await rollbackToSheetRun(runId);
      alert.success(
        `Возврат к версии выполнен: откачено запусков ${res.data.runs}, правил ${res.data.rules}, машин ${res.data.cars}, не тронуто правил ${res.data.rulesSkipped}. Автосинхронизацию лучше держать выключенной, иначе таблица вернёт цены.`
      );
      load();
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось вернуться к версии');
    } finally {
      setBusy(false);
    }
  };

  const toggleAuto = async () => {
    const next = !settings.autoSync;
    if (next && !ask('auto')) return;
    setBusy(true);
    try {
      const res = await setSheetAutoSync(next);
      setSettings(res.data);
      alert.success(next ? 'Автосинхронизация включена' : 'Автосинхронизация выключена');
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось переключить автосинхронизацию');
    } finally {
      setBusy(false);
    }
  };

  // При поиске остаются запуски, где есть подходящая позиция, и в них — только она
  const visible = useMemo(() => {
    if (!runs) return [];
    const q = query.trim();
    if (!q) return runs;
    return runs
      .map((r) => ({ ...r, changes: r.changes.filter((c) => matchesFilter(c, q)) }))
      .filter((r) => r.changes.length > 0);
  }, [runs, query]);

  if (loading && !runs) return <div className={styles.empty}>Загрузка…</div>;

  return (
    <>
      <AutoSyncPanel settings={settings} busy={busy} onToggle={toggleAuto} confirming={confirm?.kind === 'auto'} />

      <details className={styles.help}>
        <summary>Как откатываться</summary>
        <ul>
          <li>
            <b>Откатить</b> — отменяет только этот запуск: правила и цены машин, которые он менял, возвращаются к прежним.
            Если цену потом изменили ещё раз (вручную или другим запуском), её не трогаем.
          </li>
          <li>
            <b>Вернуть к версии</b> — отменяет все запуски после выбранного, от новых к старым. Выбранный запуск остаётся:
            цены и правила становятся такими, какими были сразу после него.
          </li>
          <li>
            Сама Google-таблица при откате не меняется. Поэтому после возврата к старой версии держите автосинхронизацию
            выключенной, а ручной «Применить изменения» не нажимайте, пока не поправите таблицу.
          </li>
        </ul>
      </details>

      <div className={styles.top}>
        <input
          className={styles.filter}
          placeholder="Поиск по модели, марке или ID строки"
          aria-label="Поиск по истории"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className={styles.muted}>Запуски проверки таблицы, новые сверху. Пока таблица не менялась, проверка в историю не пишет.</span>
      </div>

      {visible.length === 0 && (
        <div className={styles.empty}>{query.trim() ? `По запросу «${query}» ничего не найдено` : 'Запусков ещё не было'}</div>
      )}

      {visible.map(({ run: r, author, changes }) => {
        const id = r.id;
        const isOpen = open.has(id) || !!query.trim();
        const later = laterRollbackable(runs, id);
        return (
          <div className={styles.run} key={id}>
            <div className={styles.runHead} onClick={() => toggle(id)}>
              <span className={styles.runWhen}>{formatDateTime(r.startedAt)}</span>
              <span className={styles.kind}>{STATUS_LABELS[r.status] || r.status}</span>
              <span>{author || (r.trigger === 'schedule' ? 'авто' : '')}</span>
              <span className={styles.muted}>{summarizeRun(r)}</span>
              <span className={styles.grow} />
              {r.revertedAt && <span className={styles.reverted}>откачен {formatDateTime(r.revertedAt)}</span>}
              {canRollback(r) && later > 0 && (
                <button
                  type="button"
                  className={confirm?.kind === 'to' && confirm.id === id ? styles.miniDanger : styles.mini}
                  disabled={busy}
                  title={`Отменить все запуски после этого (${later}) и вернуть правила и цены к состоянию сразу после него`}
                  onClick={(e) => {
                    e.stopPropagation();
                    rollbackTo(id);
                  }}
                >
                  {confirm?.kind === 'to' && confirm.id === id ? `Откатить запусков: ${later}?` : 'Вернуть к версии'}
                </button>
              )}
              {canRollback(r) && (
                <button
                  type="button"
                  className={confirm?.kind === 'run' && confirm.id === id ? styles.miniDanger : styles.mini}
                  disabled={busy}
                  title="Отменить только этот запуск"
                  onClick={(e) => {
                    e.stopPropagation();
                    rollback(id);
                  }}
                >
                  {confirm?.kind === 'run' && confirm.id === id ? 'Откатить запуск?' : 'Откатить'}
                </button>
              )}
            </div>
            {isOpen && (
              <div className={styles.runBody}>
                {r.message && <div className={styles.hint}>{r.message}</div>}
                {changes.length === 0 ? (
                  <div className={styles.empty}>В этом запуске ничего не менялось</div>
                ) : (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Что</th>
                        <th>Марка</th>
                        <th className={styles.num}>Было</th>
                        <th className={styles.num}>Стало</th>
                        <th className={styles.num}>Разница</th>
                        <th>Примечание</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <span className={kindClass(c.kind)}>{KIND_LABELS[c.kind] || c.kind}</span> <b>{c.sheetKey}</b>{' '}
                            {c.label}
                          </td>
                          <td>{c.mark}</td>
                          <td className={`${styles.num} ${styles.was}`}>{money(c.oldPrice)}</td>
                          <td className={`${styles.num} ${styles.now}`}>{money(c.newPrice)}</td>
                          <td className={`${styles.num} ${isBigJump(c.oldPrice, c.newPrice) ? styles.deltaBig : ''}`}>
                            {c.kind === 'problem' ? '' : deltaText(c.oldPrice, c.newPrice)}
                          </td>
                          <td className={styles.muted}>{c.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default SheetHistory;
